import { PatientInfoFormInput } from 'containers/patient/PatientModalContainer';
import { makeCaseIdFnClinic, validInputString } from 'lib/library';
import { pageUrl } from 'lib/mapper';
import storage, { keys } from 'lib/storage';
import { IPatient, TPatientName, TRememberPatient } from 'lib/types/patientTypes';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { PatientActions, ProjectActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';

import { useQuickstartStore } from 'store/local/quickstart';

export const useQuickStart = () => {
  const {
    language,
    fetchPatientList: { list: fetchPatientList, pagingData: patientsPagingData },
    fetchPatientListSuccess,
    newPatient: { patientInfo: createPatient, msg },
    newPatientSuccess: createPatientSuccess,
    fetchPatient: { patientInfo },
    fetchPatientSuccess,
    fetchPatientFailure,
    projectsData: { list: fetchProjectList, pagingData: projectsPagingData },
    projectsSuccess: fetchProjectListSuccess,
    projectsFailure: fetchProjectListFailure,
    projectsError: fetchProjectListError,
    projectInit: { projectCount: projectCount },
    projectInitSuccess,
    createProjectData,
    createProjectSuccess,
    editProjectData,
    editProjectSuccess,
    editPatient: { patientInfo: editPatient },
    editPatientSuccess,
    installedAppList,
  }: {
    language: string;
    fetchPatientList: {
      list: IPatient[];
      pagingData: OffsetPagingData;
    };
    fetchPatientListSuccess: boolean;
    newPatient: {
      patientInfo: PatientInfoFormInput;
      msg: string;
    };
    newPatientSuccess: boolean;
    fetchPatient: {
      patientInfo: PatientInfoFormInput;
    };
    fetchPatientSuccess: boolean;
    fetchPatientFailure: boolean;
    projectsData: { list: any[]; pagingData: OffsetPagingData };
    projectsSuccess: boolean;
    projectsFailure: boolean;
    projectsError: any;
    projectInit: { projectCount: any };
    projectInitSuccess: boolean;
    createProjectData: any;
    createProjectSuccess: boolean;
    editProjectData: any;
    editProjectSuccess: boolean;
    editPatient: { patientInfo: PatientInfoFormInput };
    editPatientSuccess: boolean;
    installedAppList: string[];
  } = useShallowAppSelector((state: any) => ({
    language: state.base.language,
    fetchPatientList: state.patient.patientList.data || { list: {}, pagingData: {} },
    fetchPatientListSuccess: state.patient.patientList.success,
    newPatient: state.patient.newPatient.data || { patientInfo: {}, msg: {} },
    newPatientSuccess: state.patient.newPatient.success,
    fetchPatient: state.patient.patient.data || { patientInfo: {} },
    fetchPatientSuccess: state.patient.patient.success,
    fetchPatientFailure: state.patient.patient.failure,
    projectsData: state.project.projects.data || {},
    projectsSuccess: state.project.projects.success,
    projectsFailure: state.project.projects.failure,
    projectsError: state.project.projects.error,
    projectInit: state.project.initProject.data || { projectCount: {} },
    projectInitSuccess: state.project.initProject.success,
    createProjectData: state.project.createProject.data,
    createProjectSuccess: state.project.createProject.success,
    editProjectData: state.project.editProject.data,
    editProjectSuccess: state.project.editProject.success,
    editPatient: state.patient.editPatient.data || { patientInfo: {} },
    editPatientSuccess: state.patient.editPatient.success,
    installedAppList: state.base.installedAppList,
  }));
  const { ipcRenderer } = window;

  // SECTION: declare local state
  const { searchKeyword, prevSearchKeyword } = useQuickstartStore();

  const {
    setButtonClickType,
    setProjectPage,
    setPatientListLoadSuccess,
    setIsOpenSearchResult,
    setPatientList,
    setSearchKeyword,
    setIsChangeKeyword,
    setSearchInputRef,
    setSearchResultRef,
    setScanButtonRef,
    setProjectButtonRef,
    setRememberPatient,
    setSelectedPatientCode,
    setIsSearchFocus,
    setPrevSearchKeyword,
    setSearchData,
    setPatientPage,
    setIsOpenPatientModal,
    setPatientModalType,
  } = useQuickstartStore();
  const loginUser = storage.get(keys.user);
  const { t } = useTranslation();
  // SECTION: declare variable
  const history = useHistory();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const hasMorePatients = patientsPagingData?.page < patientsPagingData?.totalPage;
  const searchInputReference = useRef<HTMLInputElement>(null);
  const searcResultRefernce = useRef<HTMLDivElement>(null);
  const scanButtonReference = useRef<HTMLDivElement>(null);
  const projectButtonReference = useRef<HTMLDivElement>(null);

  const defaultParams = {
    stages: '',
    lastNameInitial: '',
    startDate: '',
    endDate: '',
    pageRange: 10,
    isPms: 0,
  };

  const patientNameConvert = (patient: TPatientName) => {
    const krName = `${!!patient.lastName ? patient.lastName : ''}${
      !!patient.firstName ? patient.firstName : ''
    }`;
    const enName = `${!!patient.firstName ? patient.firstName : ''}${
      !!patient.lastName ? patient.lastName : ''
    }`;
    const patientName = language === 'en' ? enName : krName;
    return patientName.trim();
  };

  const handleOpenPatientModal = (type: 'new' | 'edit') => {
    setIsOpenPatientModal(true);
    setPatientModalType(type);
  };

  const handleClosePatientModal = () => {
    setIsOpenPatientModal(false);
  };

  const handleFocusSearchBar = () => {
    setIsSearchFocus(true);
    // NOTE: Searchbar focus 시, keyword가 있는 경우, 해당 키워드로 환자 목록 API 호출
    if (!searchKeyword) return;
    setPatientListLoadSuccess(false);
    setIsOpenSearchResult(true);
    handleSearchPatient(searchKeyword);
  };

  const handleSearchInit = () => {
    setSearchKeyword('');
    setSelectedPatientCode('');
    setPatientList([]);
    setIsOpenSearchResult(false);
    setPatientListLoadSuccess(false);
    setIsSearchFocus(false);
  };

  const handleCancelKeywordChange = () => {
    const searchParamPatientName = searchParams.get('name');
    searchInputReference?.current?.blur();
    setIsChangeKeyword(false);
    handleClearState();
    setSearchKeyword(!searchParamPatientName ? '' : prevSearchKeyword);
    setIsOpenSearchResult(false);
  };
  const handleClearSearch = () => {
    setSearchKeyword('');
    setTimeout(() => {
      searchInputReference?.current?.focus();
    }, 200);
    handleClearState();
  };

  const handleClearState = () => {
    setPatientList([]);
    setIsOpenSearchResult(false);
    setPatientListLoadSuccess(false);
  };

  const handleClickOutsideSearchBar = (e: any) => {
    if (
      searcResultRefernce.current &&
      !searcResultRefernce.current.contains(e.target) &&
      scanButtonReference.current &&
      !scanButtonReference.current.contains(e.target) &&
      projectButtonReference.current &&
      !projectButtonReference.current.contains(e.target)
    ) {
      console.log('QUICK] handleClickOutsideSearchBar ______', e.target);
      handleCancelKeywordChange();
    }
  };

  const rememberPatientSetStorage = (data: TRememberPatient) => {
    const tempRememberPatient: TRememberPatient[] = [];

    //   const deleteComparison: TRememberPatient[] =
    // storage.get(keys.remember_patient)?.filter(
    //   (item: TRememberPatient) => item.code !== data.code
    // ) ?? [];

    const deleteComparison: TRememberPatient[] =
      storage
        .get(keys.remember_patient)
        ?.filter((item: TRememberPatient) => item.code !== data.code) ?? [];

    tempRememberPatient.push(data);
    if (!!deleteComparison) tempRememberPatient.push(...deleteComparison);

    // const remember =
    //   tempRememberPatient?.filter((item: TRememberPatient, index: number) => {
    //     return index < 3;
    //   }) || [];
    const remember = tempRememberPatient?.slice(0, 3) || [];

    console.log('HDS] rememberPatientSetStorage remember __ ', remember);
    storage.set(keys.remember_patient, remember);
    setRememberPatient(remember);
  };

  const handleChangeSearchKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSearchFocus(true);
    setIsChangeKeyword(true);
    setSearchKeyword(e.target.value);
  };

  const handleBlurSearchBar = () => {
    setIsChangeKeyword(false);
    setIsSearchFocus(false);
  };

  const handleSearchPatient = (data: string) => {
    setPatientListLoadSuccess(false);
    const checkText = validInputString(data);
    setSearchData(checkText);
    setSearchKeyword(checkText);

    setPatientList([]);

    PatientActions.fetch_patient_list_request({
      ...defaultParams,
      keyword: checkText,
      page: 1,
    });
    setPatientPage(1);
  };

  const handleClickSearchResult = (patient: IPatient) => {
    searchInputReference?.current?.blur();
    const patientName = patientNameConvert(patient);
    setSearchKeyword(patientName);
    setPrevSearchKeyword(patientName);
    setSelectedPatientCode(patient.patientcode);
    rememberPatientSetStorage({ name: patientName, code: patient.patientcode });

    setIsOpenSearchResult(false);
    setIsChangeKeyword(false);
    PatientActions.fetch_patient_request(patient.patientcode);

    history.replace(
      `${pageUrl.quickstart.index}?patientCode=${patient.patientcode}&name=${patientName}&page=1`,
    );
  };

  const handleClickRemember = (patient: TRememberPatient) => {
    history.replace(
      `${pageUrl.quickstart.index}?patientCode=${patient.code}&name=${patient.name}&page=1`,
    );
  };
  const handleClickQuickMenu = (
    type: 'scan' | 'project' | 'editPatient',
    pCode?: string,
    pName?: string,
  ) => {
    switch (type) {
      case 'scan':
        setButtonClickType('scan');
        if (!pCode) {
          console.log('QUICK] SCAN 환자 없음 XXXX ________');
          // 환자 생성 -> 프로젝트 생성 -> 프로젝트 상세 -> 스캔 실행
          if (!!pName) {
            handleCreatePatient(pName);
          }
          break;
        } else {
          console.log('QUICK] SCAN 환자 있음 OOOO ________');
          // 프로젝트 생성 -> 프로젝트 상세 -> 스캔 실행
          createProjectInitialize();
          break;
        }
      case 'project':
        setButtonClickType('project');
        if (!pCode) {
          console.log('QUICK] PROJECT 환자 없음 XXXX ________');
          // 환자 생성 -> 프로젝트 생성 -> 프로젝트 상세
          if (!!pName) handleCreatePatient(pName);

          break;
        } else {
          console.log('QUICK] PROJECT 환자 있음 OOOO ________');
          // 프로젝트 생성 -> 프로젝트 상세
          createProjectInitialize();
          break;
        }
        break;
      case 'editPatient':
        console.log('QUICK] EDIT PATIENT 환자 있음 OOOO ________');
        if (!pCode) break;
        handleOpenPatientModal('edit');
        break;
    }
  };

  const handleCreatePatient = (pName: string) => {
    const patient = {
      chartNumber: moment().format('YYYYMMDDHHmmss'),
      firstName: pName,
      lastName: '',
      birthday: '',
      gender: 'N',
      comment: '',
      identification: '',
      pms: '',
    };
    PatientActions.new_patient_request(patient);
  };

  const createProjectInitialize = () => {
    ProjectActions.init_project_request();
  };

  const createProject = (patient: any, count: number) => {
    const projectName = t('PROJECT_PATIENT_NAME', {
      patientName: patient?.firstName,
    });
    let clinicProjectId: string = makeCaseIdFnClinic({
      newDate: 0,
      patientName: patient?.firstName,
      groupCode: loginUser.groupCode,
      caseCount: count,
      caseIdValue: projectName,
    })(1);

    let submitData: any = {
      exocadVersion: 3,
      userIdx: loginUser.userGroupIdx,
      projectId: clinicProjectId,
      projectName: projectName,
      dueDate: null,
      senderMemo: '',
      newDate: 0,
      patientCodeIdx: patient?.patientCodeIdx,
      isMultidie: 0,
      participant: {
        client: loginUser?.userGroupIdx,
        manager: '',
        userIdx: loginUser?.userGroupIdx,
        partner: null,
      },
      copyProjectCode: '',
      copyProjectOption: [],
      remakeType: [],
      indication: {
        teeth: [],
        bridge: [],
        toothShade: 0,
        scanmodeIdx: 1,
        notation: 0,
        restorationPonticDesign: 0,
        ceramicMetalDesign: 0,
        occlusalSurfaceDesign: 0,
      },
    };

    ProjectActions.create_project_request(submitData);
  };

  const handleChangeProjectPage = (value: number) => {
    const searchParamPatientCode = searchParams.get('patientCode');
    const searchParamPatientName = searchParams.get('name');
    setProjectPage(value);
    history.replace(
      `${pageUrl.quickstart.index}?patientCode=${searchParamPatientCode}&name=${searchParamPatientName}&page=${value}`,
    );
  };

  const onFetchPatientListMore = debounce(() => {
    const { page } = patientsPagingData;
    if (!hasMorePatients) return;
    let checkText = validInputString(searchKeyword);

    PatientActions.fetch_patient_list_request({
      ...defaultParams,
      keyword: checkText,
      page: page + 1,
    });
  }, 500);

  const handleClickRememberPatient = (name: string) => {
    setSearchKeyword(name);
    setPrevSearchKeyword(name);
  };

  const handleSendExcuteApp = ({
    appName,
    projectCode,
    projectId,
    isPIM,
  }: {
    appName: string;
    projectCode: string;
    projectId: string;
    isPIM?: 0 | 1 | undefined;
  }) => {
    console.log('HDS] handleSendExcuteApp _ ', appName, ', ', projectCode, ', ', projectId);
    console.log('QUICK] executeApp _ ', appName, ', ', projectCode, ', ', projectId);
    if (!ipcRenderer) return;
    ipcRenderer.send('executeApp', {
      appName,
      projectCode,
      projectId,
      isPIM: !!isPIM ? 1 : 0,
    });
  };

  useEffect(() => {
    setProjectButtonRef(projectButtonReference);
    setScanButtonRef(scanButtonReference);
    setSearchResultRef(searcResultRefernce);
  }, []);

  useEffect(() => {
    setSearchInputRef(searchInputReference);
  }, []);

  return {
    language,
    patientNameConvert,
    projectsPagingData,
    hasMorePatients,
    onFetchPatientListMore,
    handleSearchInit,
    handleFocusSearchBar,
    handleClickOutsideSearchBar,
    handleChangeSearchKeyword,
    handleBlurSearchBar,
    handleOpenPatientModal,
    handleClosePatientModal,
    handleClickQuickMenu,
    handleCancelKeywordChange,
    handleClearSearch,
    handleClickSearchResult,
    handleChangeProjectPage,
    handleSearchPatient,
    handleClickRememberPatient,
    fetchPatientList,
    fetchPatientListSuccess,
    fetchProjectList,
    fetchProjectListSuccess,
    fetchProjectListFailure,
    fetchProjectListError,
    createPatient,
    createPatientSuccess,
    patientInfo,
    fetchPatientSuccess,
    fetchPatientFailure,
    handleClearState,
    searchInputReference,
    handleClickRemember,
    createProjectInitialize,
    projectCount,
    projectInitSuccess,
    createProject,
    createProjectData,
    createProjectSuccess,
    editProjectData,
    editProjectSuccess,
    rememberPatientSetStorage,
    handleSendExcuteApp,
    editPatient,
    editPatientSuccess,
    installedAppList,
  };
};
